/**
 * Mistake review: a reply suggested something was sent to the wrong person or using wrong data, even on a deal
 * that's already Declined/Finished. Confirm & move it back, or give Cody your own wording — either way it replies
 * to whoever flagged it and notes Credit's call on the item. "Not a mistake" is the generic Dismiss button below.
 */
import { useState } from "react";
import { Loader2, Check, PenLine, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { codyAnswerMistake } from "@/lib/api";

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

export function MistakeDecision({ run, onDone }) {
  const a = run.answer || {};
  const reply = run.inputs?.reply || {};
  const [mode, setMode] = useState("");
  const [status, setStatus] = useState(a.suggested_status || "Processing");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (decision) => {
    setBusy(true);
    try {
      const body = { decision, status, text: text || undefined };
      const r = await codyAnswerMistake(run.run_id, body);
      toast.success(r.verdict || `Moved to ${status}`); onDone?.();
    } catch (e) { err(e, "Could not action"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 border-t pt-1.5" data-testid={`cody-mistake-${run.run_id}`}>
      <div className="text-[11px] text-slate-800 flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
        <span><b>Possible mistake ({a.kind || "other"}):</b> {a.explanation}</span>
      </div>
      {reply.from && <div className="text-[10px] text-slate-500">Flagged by {reply.from}{reply.text ? ` — "${reply.text.slice(0, 160)}"` : ""}</div>}
      <div className="flex flex-wrap items-center gap-1.5">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-7 text-xs w-32" data-testid={`cody-mistake-status-${run.run_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Processing">Processing</SelectItem>
            <SelectItem value="UW - IR">UW - IR</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => go("confirm")} data-testid={`cody-mistake-confirm-${run.run_id}`}>
          <Check className="w-3 h-3 mr-1" /> Confirm & move back
        </Button>
        <Button size="sm" variant={mode === "own_wording" ? "default" : "outline"} className="h-7 text-xs" disabled={busy} onClick={() => setMode(mode === "own_wording" ? "" : "own_wording")} data-testid={`cody-mistake-own-wording-${run.run_id}`}>
          <PenLine className="w-3 h-3 mr-1" /> Own wording
        </Button>
        {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </div>
      {mode === "own_wording" && (
        <div className="space-y-1">
          <Textarea className="text-[11px] min-h-[60px]" placeholder="What to tell whoever flagged it (sent as the reply)." value={text} onChange={(e) => setText(e.target.value)} data-testid={`cody-mistake-own-wording-text-${run.run_id}`} />
          <Button size="sm" className="h-7 text-xs" disabled={busy || !text.trim()} onClick={() => go("own_wording")} data-testid={`cody-mistake-own-wording-send-${run.run_id}`}>Send this</Button>
        </div>
      )}
    </div>
  );
}
